import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Chip, IconButton } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CompanyComparisonChart = ({ 
  companies, // 새로운 데이터 구조
  selectedKPIs, 
  availableKPIs, 
  isDarkMode,
  onKPIChange,
  selectedPeriod, // 선택된 기간 (DAY/WEEK/MONTH)
  periodLabel, // 기간 라벨 (일별/주별/월별)
  comparisonPeriodText // 비교 기간 텍스트
}) => {
  // KPI별 고정 색상 매핑 (KPI ID를 기준으로)
  const getKPIColor = (kpiId) => {
    const colorMap = {
      'ENERGY_EFF': '#2196f3',    // 에너지 효율 - 파란색
      'PROD_RATE': '#4caf50',     // 생산량 - 초록색  
      'DEFECT_RATE': '#ff9800',   // 불량률 - 주황색
      'QUALITY_SCORE': '#f44336', // 품질 점수 - 빨간색
      'COST_RATE': '#9c27b0',     // 비용률 - 보라색
      'DELIVERY_RATE': '#00bcd4', // 납기율 - 시안색
      'SAFETY_SCORE': '#8bc34a'   // 안전 점수 - 연두색
    };
    
    // 매핑된 색상이 있으면 사용, 없으면 기본 색상들에서 선택
    if (colorMap[kpiId]) {
      return colorMap[kpiId];
    }
    
    // 기본 색상 배열에서 해시 기반으로 선택
    const defaultColors = ['#ffc107', '#e91e63', '#795548', '#607d8b', '#3f51b5'];
    const hash = kpiId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return defaultColors[hash % defaultColors.length];
  };
  
  const [visibleKPIs, setVisibleKPIs] = useState([]);
  const chartRef = useRef(null);

  // 초기 로딩 시 모든 KPI를 선택된 상태로 설정
  useEffect(() => {
    if (companies && companies.length > 0) {
      const allKPIs = new Set();
      companies.forEach(company => {
        company.subscriptions?.forEach(subscription => {
          if (subscription.kpiData && subscription.kpiData.length > 0) {
            // categoryId + kpiIndicatorCode 조합으로 고유 식별
            allKPIs.add(`${subscription.categoryId}-${subscription.kpiIndicatorCode}`);
          }
        });
      });

      // visibleKPIs가 비어있는 경우에만 모든 KPI를 설정
      if (visibleKPIs.length === 0 && allKPIs.size > 0) {
        setVisibleKPIs(Array.from(allKPIs));
      }
    }
  }, [companies, visibleKPIs.length]);

  useEffect(() => {
    return () => {
      const chart = chartRef.current;
      if (chart && chart.chartInstance) {
        chart.chartInstance.destroy();
      }
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (chart && chart.chartInstance) {
      chart.chartInstance.destroy();
    }
  }, [selectedKPIs, companies]);

  if (!companies || companies.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          회사 데이터를 불러오는 중입니다...
        </Typography>
      </Box>
    );
  }

  // 카테고리 코드를 카테고리명으로 변환
  const getCategoryName = (categoryId) => {
    const categoryMap = {
      'E': '에너지',
      'P': '생산',
      'Q': '품질',
      'C': '비용',
      'D': '납기',
      'S': '안전'
    };
    return categoryMap[categoryId] || categoryId;
  };


  // 모든 회사의 구독 KPI를 수집하여 카테고리별로 그룹화
  const getAllAvailableKPIs = () => {
    const allKPIs = new Map();

    // 모든 회사의 구독 정보에서 KPI 수집
    companies.forEach(company => {
      company.subscriptions.forEach(subscription => {
        // categoryId + kpiIndicatorCode 조합으로 고유 식별
        const kpiKey = `${subscription.categoryId}-${subscription.kpiIndicatorCode}`;
        if (!allKPIs.has(kpiKey)) {
          allKPIs.set(kpiKey, {
            id: kpiKey,
            name: subscription.kpiIndicatorName,
            categoryId: subscription.categoryId,
            categoryName: getCategoryName(subscription.categoryId),
            kpiIndicatorCode: subscription.kpiIndicatorCode,
          });
        }
      });
    });

    return Array.from(allKPIs.values());
  };

  const availableKPIsList = getAllAvailableKPIs();

  // KPI 표시 여부 토글
  const toggleKPIVisibility = (kpiId) => {
    if (visibleKPIs.includes(kpiId)) {
      setVisibleKPIs(prev => prev.filter(id => id !== kpiId));
    } else {
      setVisibleKPIs(prev => [...prev, kpiId]);
    }
  };

  // 전체 회사의 기간비교 데이터를 가져오는 함수 (업체별 모든 KPI 비교)
  const getCompaniesComparisonData = () => {
    const datasets = [];
    const companyNames = companies.map(company => company.companyName);

    // 모든 구독 KPI를 수집 (categoryId + kpiIndicatorCode 조합으로)
    const allKPIs = new Set();
    companies.forEach(company => {
      company.subscriptions.forEach(subscription => {
        if (subscription.kpiData && subscription.kpiData.length > 0) {
          allKPIs.add(`${subscription.categoryId}-${subscription.kpiIndicatorCode}`);
        }
      });
    });

    // visibleKPIs가 비어있으면 모든 KPI를 표시, 아니면 선택된 KPI만 표시
    const visibleKPIList = visibleKPIs.length === 0 ? Array.from(allKPIs) : Array.from(allKPIs).filter(kpiId => visibleKPIs.includes(kpiId));

    // 각 KPI에 대한 데이터셋 생성 (표시할 KPI만) - 같은 KPI끼리 그룹화
    visibleKPIList.forEach((kpiId, kpiIndex) => {
      const kpi = availableKPIsList.find(k => k.id === kpiId);
      if (!kpi) return;

      const baseColor = getKPIColor(kpiId);

      // 이전 기간 데이터 (회사별로 구독 여부에 따라 데이터 또는 null)
      const previousData = companies.map(company => {
        const subscription = company.subscriptions.find(sub =>
          sub.categoryId === kpi.categoryId && sub.kpiIndicatorCode === kpi.kpiIndicatorCode && sub.kpiData?.length > 0
        );

        if (subscription) {
          const latestData = subscription.kpiData[subscription.kpiData.length - 1];
          // 실제 이전 기간 측정값 사용
          const previousValue = latestData.previousPeriodValue || 0;
          return Math.max(0, previousValue);
        }
        return null; // 구독하지 않는 경우 null로 처리
      });

      // 현재 기간 데이터 (회사별로 구독 여부에 따라 데이터 또는 null)
      const currentData = companies.map(company => {
        const subscription = company.subscriptions.find(sub =>
          sub.categoryId === kpi.categoryId && sub.kpiIndicatorCode === kpi.kpiIndicatorCode && sub.kpiData?.length > 0
        );

        if (subscription) {
          const latestData = subscription.kpiData[subscription.kpiData.length - 1];
          return Math.max(0, latestData.measuredValue || 0);
        }
        return null; // 구독하지 않는 경우 null로 처리
      });

      // 이전 기간 데이터셋 (연한 색) - 같은 KPI 그룹의 첫 번째
      datasets.push({
        id: `${kpiId}-previous`,
        label: `${kpi.name} (이전 ${periodLabel})`,
        data: previousData,
        backgroundColor: baseColor + '99', // 투명도 60%
        borderWidth: 0,
        borderRadius: 4,
        barThickness: 14,
        maxBarThickness: 16,
        skipNull: true,
        order: kpiIndex * 2 + 1 // 같은 KPI 그룹에서 이전 데이터가 먼저
      });

      // 현재 기간 데이터셋 (진한 색) - 같은 KPI 그룹의 두 번째
      datasets.push({
        id: `${kpiId}-current`,
        label: `${kpi.name} (현재 ${periodLabel})`,
        data: currentData,
        backgroundColor: baseColor,
        borderWidth: 0,
        borderRadius: 4,
        barThickness: 14,
        maxBarThickness: 16,
        skipNull: true,
        order: kpiIndex * 2 // 같은 KPI 그룹에서 현재 데이터가 나중
      });
    });

    return { labels: companyNames, datasets };
  };
  
  const companiesData = getCompaniesComparisonData();
  
  const chartData = companiesData;


  // 데이터 최대값 계산
  const maxValue = chartData.datasets.length > 0 ? Math.max(
    ...chartData.datasets.flatMap(dataset => 
      dataset.data.filter(val => val !== null).map(val => Number(val) || 0)
    )
  ) : 0;

  // 회사별 목표값과 차이값 정보를 가져오는 함수
  const getCompanyTargetInfo = (companyIndex) => {
    const company = companies[companyIndex];
    if (!company || !company.subscriptions) return [];

    const info = [];
    company.subscriptions.forEach(subscription => {
      if (subscription.kpiData && subscription.kpiData.length > 0) {
        const kpiId = `${subscription.categoryId}-${subscription.kpiIndicatorCode}`;
        if (visibleKPIs.length === 0 || visibleKPIs.includes(kpiId)) {
          const latestData = subscription.kpiData[subscription.kpiData.length - 1];
          const targetValue = subscription.targetValue || 0;
          const currentValue = latestData.measuredValue || 0;
          const previousValue = latestData.previousPeriodValue || 0;
          const periodComparison = currentValue - previousValue;

          // 단위 정보 가져오기
          const unit = latestData.unit || '';

          info.push({
            name: subscription.kpiIndicatorName,
            target: Math.floor(targetValue * 100) / 100,
            difference: Math.floor(periodComparison * 100) / 100,
            unit: unit
          });
        }
      }
    });
    return info;
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    grouped: true, // 바들을 그룹별로 나란히 배치
    interaction: {
      intersect: false,
      mode: 'index'
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
      },
      tooltip: {
        enabled: false, // 기본 tooltip 비활성화
        external: function(context) {
          // External tooltip 구현
          const { chart, tooltip } = context;

          // tooltip 요소 생성 또는 가져오기
          let tooltipEl = chart.canvas.parentNode.querySelector('.chartjs-tooltip');

          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.className = 'chartjs-tooltip custom-white-tooltip';
            chart.canvas.parentNode.appendChild(tooltipEl);

            // CSS 스타일을 헤드에 추가
            if (!document.getElementById('tooltip-styles')) {
              const style = document.createElement('style');
              style.id = 'tooltip-styles';
              style.textContent = `
                .custom-white-tooltip {
                  background: white !important;
                  background-color: white !important;
                  color: #1f2937 !important;
                  border: 1px solid #e5e7eb !important;
                  border-radius: 8px !important;
                  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12), 0 3px 6px rgba(0, 0, 0, 0.08) !important;
                }
                .custom-white-tooltip * {
                  color: inherit !important;
                }
              `;
              document.head.appendChild(style);
            }
          }

          // 인라인 스타일 재설정
          tooltipEl.style.cssText = `
            background: white !important;
            background-color: white !important;
            border-radius: 10px !important;
            color: #1f2937 !important;
            opacity: 0 !important;
            position: absolute !important;
            transition: opacity 0.3s ease, transform 0.3s ease !important;
            pointer-events: none !important;
            padding: 12px 14px !important;
            max-width: 280px !important;
            font-size: 13px !important;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
            box-shadow: 0 12px 25px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.1) !important;
            border: 1px solid #e5e7eb !important;
            z-index: 1000 !important;
            transform: translateY(10px) !important;
          `;

          // CSS에 말풍선 화살표 추가
          tooltipEl.style.setProperty('--arrow-size', '8px');
          tooltipEl.innerHTML = `
            <style>
              .tooltip-arrow {
                position: absolute;
                bottom: -8px;
                left: 50%;
                transform: translateX(-50%);
                width: 0;
                height: 0;
                border-left: 8px solid transparent;
                border-right: 8px solid transparent;
                border-top: 8px solid white;
                filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
              }
            </style>
            <div class="tooltip-content"></div>
            <div class="tooltip-arrow"></div>
          `;

          // tooltip 숨기기
          if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
          }

          // 데이터 가져오기
          if (tooltip.body) {
            const dataPoints = tooltip.dataPoints;
            const dataIndex = dataPoints[0].dataIndex;
            const company = companies[dataIndex];
            const companyName = company.companyName;

            // KPI 데이터 필터링 (구독중인 것만)
            const kpiLabels = [];
            dataPoints.forEach(dataPoint => {
              const value = dataPoint.parsed.y;
              const kpiName = dataPoint.dataset.label;
              const kpiId = dataPoint.dataset.id;

              if (!kpiId || !company || !company.subscriptions) return;

              const [categoryId, kpiIndicatorCode] = kpiId.replace(/-(previous|current)$/, '').split('-');
              const hasSubscription = company.subscriptions.some(sub =>
                sub.categoryId === categoryId && sub.kpiIndicatorCode === kpiIndicatorCode
              );

              if (hasSubscription) {
                // 단위 정보 가져오기
                const subscription = company.subscriptions.find(sub =>
                  sub.categoryId === categoryId && sub.kpiIndicatorCode === kpiIndicatorCode
                );
                const unit = subscription?.kpiData?.[0]?.unit || '';

                const displayValue = value !== null && !isNaN(value) ? Math.floor(value * 100) / 100 : value;
                kpiLabels.push(`${kpiName}: ${displayValue !== null ? displayValue.toFixed(2) : '없음'}${unit ? ' ' + unit : ''}`);
              }
            });

            // 목표값 및 차이 정보
            const targetInfo = getCompanyTargetInfo(dataIndex);

            // HTML 생성 - 모든 내용 포함하면서 컴팩트하게
            let innerHtml = `
              <div style="margin-bottom: 8px;">
                <div style="font-size: 14px; font-weight: 600; color: #1f2937; margin-bottom: 5px;">
                  ${companyName}
                </div>
              </div>
            `;

            // KPI 데이터 표시 (모든 KPI)
            if (kpiLabels.length > 0) {
              kpiLabels.forEach(label => {
                innerHtml += `<div style="color: #4b5563; margin-bottom: 3px; font-size: 12px;">${label}</div>`;
              });
            }

            // 목표값 및 차이 섹션 (모든 KPI)
            if (targetInfo.length > 0) {
              targetInfo.forEach(info => {
                let diffIcon, diffColor;
                if (info.difference === 0) {
                  diffIcon = '━';
                  diffColor = '#6b7280';
                } else if (info.difference > 0) {
                  diffIcon = '🔺';
                  diffColor = '#059669';
                } else {
                  diffIcon = '🔻';
                  diffColor = '#dc2626';
                }

                innerHtml += `
                  <div style="margin-bottom: 4px; padding: 6px; background: rgba(243, 244, 246, 0.6); border-radius: 4px; border-left: 3px solid ${diffColor};">
                    <div style="font-size: 12px; font-weight: 500; color: #1f2937; margin-bottom: 2px;">
                      ${info.name}
                    </div>
                    <div style="display: flex; justify-content: space-between; font-size: 11px;">
                      <span style="color: #d97706;">🎯 목표: ${info.target}${info.unit ? ' ' + info.unit : ''}</span>
                      <span style="color: ${diffColor};">${diffIcon} ${info.difference >= 0 ? '증가' : '감소'}: ${info.difference >= 0 ? '+' : ''}${info.difference}${info.unit ? ' ' + info.unit : ''}</span>
                    </div>
                  </div>
                `;
              });
            }

            const tooltipContent = tooltipEl.querySelector('.tooltip-content');
            if (tooltipContent) {
              tooltipContent.innerHTML = innerHtml;
            } else {
              tooltipEl.innerHTML = innerHtml;
            }
          }

          // 위치 설정 - 항상 위쪽에 표시
          const canvasRect = chart.canvas.getBoundingClientRect();
          const tooltipWidth = tooltipEl.offsetWidth || 280;
          const tooltipHeight = tooltipEl.offsetHeight || 120;

          // 해당 회사의 그래프 영역 중간 위치 계산
          const dataIndex = tooltip.dataPoints[0].dataIndex;
          const chartArea = chart.chartArea;
          const totalCompanies = chartData.labels.length;
          const companyWidth = chartArea.width / totalCompanies;
          const companyStartX = chartArea.left + (dataIndex * companyWidth);
          const companyCenterX = companyStartX + (companyWidth / 2);

          let left = companyCenterX - (tooltipWidth / 2);
          let top = tooltip.caretY - tooltipHeight - 20; // 마우스 위쪽에 20px 여백 (화살표 공간 포함)

          // 좌우 경계 체크
          if (left < 10) {
            left = 10;
          } else if (left + tooltipWidth > canvasRect.width - 10) {
            left = canvasRect.width - tooltipWidth - 10;
          }

          // 상단 경계 체크 - 위쪽 공간이 부족해도 위쪽에 표시 (차트 밖으로 나가더라도)
          if (top < -50) {
            top = -50; // 최소한의 여백만 유지
          }

          // 화살표 위치 조정 - 회사 영역 중간을 가리키도록
          const arrowOffset = companyCenterX - left - (tooltipWidth / 2);
          const arrow = tooltipEl.querySelector('.tooltip-arrow');
          if (arrow) {
            arrow.style.left = `calc(50% + ${arrowOffset}px)`;
            // 화살표가 툴팁 범위를 벗어나지 않도록 제한
            const maxOffset = (tooltipWidth / 2) - 16;
            if (Math.abs(arrowOffset) > maxOffset) {
              arrow.style.left = arrowOffset > 0 ? `calc(100% - 16px)` : '16px';
            }
          }

          // 부드러운 애니메이션으로 표시
          tooltipEl.style.left = left + 'px';
          tooltipEl.style.top = top + 'px';

          // 애니메이션 효과
          setTimeout(() => {
            tooltipEl.style.opacity = '1';
            tooltipEl.style.transform = 'translateY(0)';
          }, 10);
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: maxValue > 0 ? Math.max(maxValue * 1.1, maxValue + 100) : 1000,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          drawBorder: false
        },
        ticks: {
          color: '#666',
          callback: function(value) {
            return value;
          }
        }
      },
      x: {
        type: 'category',
        grid: {
          display: false
        },
        ticks: {
          color: '#666',
          font: {
            size: 12,
            weight: 'bold'
          },
          maxRotation: 45,
          minRotation: 0
        },
        categoryPercentage: 0.8, // 카테고리별 전체 너비 비율
        barPercentage: 0.9 // 각 바의 너비 비율
      }
    },
    elements: {
      bar: {
        borderWidth: 0, // 테두리 제거
      }
    }
  };


  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" fontWeight="600" sx={{ color: '#333' }}>
            업체별 KPI 실제 측정값 비교 - 기간 대비
          </Typography>
          <Chip 
            label={periodLabel}
            sx={{
              backgroundColor: '#e3f2fd',
              color: '#1976d2',
              fontWeight: 'bold',
              fontSize: '0.8rem'
            }}
          />
          {/* 비교 기간 정보 */}
          <Typography variant="body2" sx={{ color: '#333', fontSize: '0.9rem', fontWeight: 500 }}>
            ({comparisonPeriodText})
          </Typography>
        </Box>
        
        {/* 개선/악화 요약 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
            모든 구독 KPI의 이전 기간 대비 현재 실제 측정값 비교
          </Typography>
        </Box>
      </Box>
      
      
      {/* 커스텀 KPI 범례 */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1.5, color: '#666', fontWeight: 500 }}>
          표시할 KPI 선택
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {availableKPIsList.map((kpi, index) => {
            const isVisible = visibleKPIs.includes(kpi.id);
            const kpiColor = getKPIColor(kpi.id);
            
            return (
              <Box key={kpi.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  onClick={() => toggleKPIVisibility(kpi.id)}
                  size="small"
                  sx={{
                    padding: '2px',
                    '&:hover': {
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  {isVisible ? (
                    <CheckCircleIcon 
                      sx={{ 
                        color: kpiColor, 
                        fontSize: 20,
                        '& path': {
                          stroke: 'white',
                          strokeWidth: 1
                        }
                      }} 
                    />
                  ) : (
                    <RadioButtonUncheckedIcon 
                      sx={{ 
                        color: kpiColor + '50', // 50% 투명도
                        fontSize: 20 
                      }} 
                    />
                  )}
                </IconButton>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: isVisible ? '#333' : '#999',
                    fontWeight: isVisible ? 600 : 400,
                    fontSize: '0.85rem',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleKPIVisibility(kpi.id)}
                >
                  {kpi.name}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {chartData.datasets.length > 0 ? (
        <Box sx={{ height: '400px', position: 'relative' }}>
          <Bar ref={chartRef} data={chartData} options={options} />
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8, backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <Typography variant="h6" color="text.secondary" mb={1}>
            표시할 KPI 데이터가 없습니다
          </Typography>
          <Typography variant="body2" color="text.secondary">
            회사별 KPI 구독 정보와 데이터를 확인해주세요
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CompanyComparisonChart;